<template>
    <PanelFrame ref="panelFrame" panel-class="billing-app" resize-directions="all" :content-class="panelContentClass">
        <template #titlebar-left>
            <button v-if="isCompact && mobileView === 'main'" class="panel-titlebar-btn" @click.stop="mobileView = 'sidebar'">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <button v-else class="panel-titlebar-btn" @click.stop="sidebarOpen = !sidebarOpen" :title="$t('common.billing.sidebar')">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="2" y="2" width="20" height="20" rx="2" ry="2"/><line x1="9" y1="2" x2="9" y2="22"/>
                </svg>
            </button>
            <span class="billing-titlebar-title">{{ $t('common.billing.title') }}</span>
        </template>

        <template #default>
            <div class="billing-body">
                <BillingSidebar v-if="showSidebar"
                    :width="isCompact ? null : sidebarWidth"
                    @navigate="onNavigate"
                    @divider-start="startSidebarResize"/>
                <div v-show="showMain" class="billing-main-wrap">
                    <BillingPackages v-if="bs.state.activeSection === 'packages'"/>
                    <BillingModules v-else-if="bs.state.activeSection === 'modules'"/>
                    <BillingOrders v-else-if="bs.state.activeSection === 'orders'"/>
                    <BillingOrderDetail v-else-if="bs.state.activeSection === 'orderDetail'"/>
                    <BillingWallet v-else-if="bs.state.activeSection === 'wallet'"/>
                    <BillingCards v-else-if="bs.state.activeSection === 'cards'"/>
                    <BillingCheckout/>
                </div>
            </div>
            <BillingPaymentResult/>
        </template>
    </PanelFrame>
</template>

<script>
import './billing-panel.css'
import { createPanelMixin } from '@/components/panel/usePanelBase'
import PanelFrame from '@/components/panel/PanelFrame.vue'
import BillingSidebar from './BillingSidebar.vue'
import BillingPackages from './BillingPackages.vue'
import BillingModules from './BillingModules.vue'
import BillingOrders from './BillingOrders.vue'
import BillingOrderDetail from './BillingOrderDetail.vue'
import BillingWallet from './BillingWallet.vue'
import BillingCards from './BillingCards.vue'
import BillingCheckout from './BillingCheckout.vue'
import BillingPaymentResult from './BillingPaymentResult.vue'
import billingService from './billingService'

const billingPanelMixin = createPanelMixin({
    panelId: 'billingPanel',
    shortcut: { code: 'KeyB' },
    minWidth: 420,
    minHeight: 480,
    defaults: {
        floatingMode: false,
        width: 960,
        height: 680,
        sidebarWidth: 220,
    },
    hasInnerLayout: true,
    showSettingsInCompact: true,
    minSidebar: 170,
    maxSidebar: 280,
    settingsItems: [
        'alwaysOnTop',
        'openOnLoad',
        'divider',
        'transparent',
        'divider',
        'resetWindowSize',
        'resetInnerLayout',
    ],
    onEscape(vm) {
        if (billingService.state.externalPaymentResult) {
            billingService.clearExternalPaymentResult()
            return true
        }
        if (billingService.state.checkoutOpen) {
            if (billingService.state.paymentStatus === 'processing') return true
            billingService.closeCheckout()
            return true
        }
        return false
    },
    onContext(vm, ctx) {
        if (ctx && ctx.orderId) {
            billingService.selectOrder(ctx.orderId)
        }
    },
    extraStorageKeys(vm) {
        return { sidebarOpen: vm.sidebarOpen }
    },
})

export default {
    name: 'BillingPanel',
    mixins: [billingPanelMixin],
    components: { PanelFrame, BillingSidebar, BillingPackages, BillingModules, BillingOrders, BillingOrderDetail, BillingWallet, BillingCards, BillingCheckout, BillingPaymentResult },
    data() {
        const saved = this.panelLoadLayout()
        return {
            sidebarOpen: saved.sidebarOpen ?? true,
            mobileView: 'sidebar',
        }
    },
    computed: {
        bs() {
            return billingService
        },
        panelContentClass() {
            return 'billing-active'
        },
        showSidebar() {
            return this.isCompact ? this.mobileView === 'sidebar' : this.sidebarOpen
        },
        showMain() {
            return !this.isCompact || this.mobileView === 'main'
        },
    },
    methods: {
        onResetInnerLayout() {
            this.sidebarWidth = 220
            this.sidebarOpen = true
            this.panelSaveLayoutDebounced()
        },
        onNavigate(section) {
            billingService.setSection(section)
            if (this.isCompact) {
                this.mobileView = 'main'
            }
        },
    },
    watch: {
        sidebarOpen() { this.panelSaveLayoutDebounced() },
    },
    mounted() {
        this.$nextTick(() => {
            this.$watch('panelWidth', (w, old) => {
                if (!this.isCompact && w < 520 && old >= 520 && this.sidebarOpen) {
                    this.sidebarOpen = false
                }
            })
        })
    },
}
</script>
